import { List, Map } from "immutable";
import { find } from "lodash";

import { GLOBAL_FILTER_ID } from "../../constants/identifiers";
import { createFromJS, immutableRecords } from "../../types";
import treeUtils from "../../utils/tree";
import { appStateActionTypes, appStateSelectors } from "../appState";
import { getFirstViewFromLayout, nextViewInLayout } from "../appState/reducers/utils";
import combineReducersWithGlobal from "../combineReducersWithGlobal";
import { definitionActionTypes } from "../definition";
import { itemActionTypes } from "../items";
import { narrativeActionTypes } from "../narrative";
import { relationshipActionTypes } from "../relationships";
import { savedFilterActionTypes } from "../savedFilters";
import { actionTypes } from "./actions";

const createFilter = () => {
  return immutableRecords.FilterRecord();
};

const copyFilter = f => {
  return f ? f : createFilter();
};

const updateListFilter = (f, terms, match) => {
  return f.merge({
    terms: List(terms),
    match
  });
};

const removeTermsFromListFilter = (f, removedTerms) => {
  return f.update("terms", terms => terms.filter(t => removedTerms.indexOf(t) < 0));
};

const updateRangeFilter = (f, above, below) => {
  return f.merge({
    above,
    below
  });
};

const updateNarrativeFilter = (f, narrativeStatus, from, to) => {
  return f.merge({
    narrativeStatus,
    from,
    to
  });
};

const updateBoolFilter = (f, expected) => {
  return f.set("expected", expected);
};

const updateFilterByIdWhenSettingNumViewsInner = (
  filterByIdState,
  numViews,
  layout,
  viewsCount,
  activeViewId,
  newViewIds
) => {
  let newFilterState = filterByIdState;
  if (viewsCount < numViews) {
    const diff = numViews - viewsCount;
    for (let i = 0; i < diff; i++) {
      // same as split horizontal
      newFilterState = newFilterState.set(newViewIds[i], copyFilter(newFilterState.get(activeViewId)));
    }
  } else if (viewsCount > numViews) {
    let nextViewId = activeViewId;
    let keepIds = [nextViewId];
    for (let i = 0; i < numViews - 1; i++) {
      nextViewId = nextViewInLayout(layout, nextViewId) || getFirstViewFromLayout(layout);
      keepIds.push(nextViewId);
    }
    newFilterState = newFilterState.filter((_f, id) => id === GLOBAL_FILTER_ID || keepIds.includes(id));
  }
  return newFilterState;
};

/**
 * This function can set more than two views.
 * Used to set three views that has been removed from the app.
 * @param {Immutable.Map} filterByIdState
 * @param {number} numViews Number of the views
 * @param {string[]} newViewIds
 * @param {TimelineRecord} globalState
 */
const updateFilterByIdWhenSettingNumViews = (filterByIdState, numViews, newViewIds, globalState) => {
  const layout = appStateSelectors.getRootLayout(globalState);
  const viewsCount = appStateSelectors.getOpenViewCount(globalState);
  const activeViewId = appStateSelectors.getActiveViewId(globalState);
  return updateFilterByIdWhenSettingNumViewsInner(
    filterByIdState,
    numViews,
    layout,
    viewsCount,
    activeViewId,
    newViewIds
  );
};

const removeItemsFromNarrativeFilter = (state, removeIds, globalState) => {
  let result = state;
  const narrative = globalState.data.narrative;
  // If the from item is amongst those being removed, then adjust to the next
  // sibling AFTER that one, or otherwise set to null.
  if (state.from && removeIds.indexOf(state.from) >= 0) {
    const foundParentAndIndex = treeUtils.findParentAndIndexOfNode(narrative, state.from);
    let nextSibling = null;
    if (foundParentAndIndex && foundParentAndIndex.parent) {
      const { parent, index } = foundParentAndIndex;
      const parentChildren = treeUtils.getChildIdsOfParent(narrative, parent);
      // Find the next sibling at this level that is not being removed, or null if none exist
      nextSibling = find(parentChildren, (id, i) => i > index && removeIds.indexOf(id) === -1);
    }
    result = result.set("from", nextSibling);
  }
  if (state.to && removeIds.indexOf(state.to) >= 0) {
    const foundParentAndIndex = treeUtils.findParentAndIndexOfNode(narrative, state.to);
    let prevSibling = null;
    if (foundParentAndIndex && foundParentAndIndex.parent) {
      const { parent, index } = foundParentAndIndex;
      const parentChildren = treeUtils.getChildIdsOfParent(narrative, parent);
      // Find the next sibling at this level that is not being removed, or null if none exist
      prevSibling = find(parentChildren, (id, i) => i < index && removeIds.indexOf(id) === -1);
    }
    result = result.set("to", prevSibling);
  }
  return result;
};

const byId = (state = Map(), action, globalState) => {
  // const activeViewId = appStateSelectors.getActiveViewId(globalState);
  switch (action.type) {
    case appStateActionTypes.SPLIT_VIEW_HORIZONTAL:
    case appStateActionTypes.SPLIT_VIEW_VERTICAL:
      return state.set(action.viewId, copyFilter(state.get(action.splittingViewId)));
    case appStateActionTypes.REMOVE_VIEW:
      return state.filter((_f, id) => id !== action.viewId);
    case appStateActionTypes.SET_LAYOUT_SINGLE_VIEW:
    case appStateActionTypes.REMOVE_OTHER_VIEWS:
      return state.filter((_f, id) => id === GLOBAL_FILTER_ID || id === appStateSelectors.getActiveViewId(globalState));
    case appStateActionTypes.SET_LAYOUT_TWO_VIEW_STACK:
    case appStateActionTypes.SET_LAYOUT_TWO_VIEW_STACK_THIRDS:
    case appStateActionTypes.SET_LAYOUT_TWO_VIEW_SIDE: {
      return updateFilterByIdWhenSettingNumViews(state, 2, [action.viewId], globalState);
    }
    //#!if readonly !== true
// case savedFilterActionTypes.SAVE_FILTER:
// return state.set(action.newFilterId, copyFilter(state.get(action.origFilterId)));
// case savedFilterActionTypes.DELETE_SAVED_FILTER:
// return state.filter((f, id) => id !== action.filterId);
    //#!endif
    case actionTypes.CLEAR_FILTER: {
      return state.set(action.filterId, createFilter());
    }
    case actionTypes.LOAD_FILTER: {
      return state.set(action.targetFilterId, state.get(action.sourceFilterId));
    }
    case actionTypes.CHANGE_FILTER_MATCH_CRITERIA: {
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.set("matchCriteria", action.match)
      );
    }
    case actionTypes.CHANGE_LABEL_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("label", v => updateListFilter(v, action.terms, action.match))
      );
    case actionTypes.CHANGE_SUMMARY_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("summary", v => updateListFilter(v, action.terms, action.match))
      );
    case actionTypes.CHANGE_TAGS_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("tags", v => updateListFilter(v, action.terms, action.match))
      );
    case actionTypes.CHANGE_TYPE_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("type", v => updateListFilter(v, action.terms, action.match))
      );
    case actionTypes.CHANGE_COLOR_FILTER: {
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("color", v => updateListFilter(v, action.terms, action.match))
      );
    }
    case actionTypes.CHANGE_SUPER_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("super", v => updateListFilter(v, action.terms, action.match))
      );
    case actionTypes.CHANGE_DATE_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("date", v => updateRangeFilter(v, action.above, action.below))
      );
    case actionTypes.CHANGE_DURATION_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("duration", v => updateRangeFilter(v, action.above, action.below))
      );
    case actionTypes.CHANGE_NARRATIVE_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("narrative", v => updateNarrativeFilter(v, action.narrativeStatus, action.from, action.to))
      );
    case actionTypes.CHANGE_TYPE_REFERENCE_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("relationshipsByType", (ref = Map()) =>
          ref.update(action.typeId, (v = immutableRecords.ListFilterPartRecord()) =>
            updateListFilter(v, action.terms, action.match)
          )
        )
      );
    case actionTypes.CHANGE_REFERENCE_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("relationshipsByReference", (ref = Map()) =>
          ref.update(action.referenceId, (v = immutableRecords.ListFilterPartRecord()) =>
            updateListFilter(v, action.terms, action.match)
          )
        )
      );
    case actionTypes.CHANGE_TEXT_PROPERTY_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("textProperties", (p = Map()) =>
          p.update(action.propertyId, (v = immutableRecords.ListFilterPartRecord()) =>
            updateListFilter(v, action.terms, action.match)
          )
        )
      );
    case actionTypes.CHANGE_NUMERIC_PROPERTY_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("numericProperties", (p = Map()) =>
          p.update(action.propertyId, (v = immutableRecords.RangeFilterPartRecord()) =>
            updateRangeFilter(v, action.above, action.below)
          )
        )
      );
    case actionTypes.CHANGE_BOOL_PROPERTY_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("boolProperties", (p = Map()) =>
          p.update(action.propertyId, (v = immutableRecords.BoolFilterPartRecord()) =>
            updateBoolFilter(v, action.expected)
          )
        )
      );
    //#!if readonly !== true
// case itemActionTypes.DELETE_ITEMS: {
// return state.map(f =>
// f
// .update("relationshipsByReference", ref => ref.map(v => removeTermsFromListFilter(v, action.ids)))
// .update("relationshipsByType", ref => ref.map(v => removeTermsFromListFilter(v, action.ids)))
// .update("narrative", narrative => removeItemsFromNarrativeFilter(narrative, action.ids, globalState))
// );
// }
// case narrativeActionTypes.REMOVE_FROM_NARRATIVE: {
// return state.map(f =>
// f.update("narrative", narrative => removeItemsFromNarrativeFilter(narrative, action.ids, globalState))
// );
// }
// case relationshipActionTypes.DELETE_RELATIONSHIPS:
// return state.map(f =>
// f
// .update("relationshipsByReference", ref => ref.map(v => removeTermsFromListFilter(v, action.ids)))
// .update("relationshipsByType", ref => ref.map(v => removeTermsFromListFilter(v, action.ids)))
// );
    //#!endif
    case definitionActionTypes.REPLACE_DEFINITIONS: {
      return action.definitions.includeNarrative
        ? state
        : state.map(f => f.set("narrative", immutableRecords.RangeFilterPartRecord()));
    }

    case actionTypes.REMOVE_REFERENCE_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("relationshipsByReference", (ref = Map()) => ref.delete(action.referenceId))
      );
    case actionTypes.REMOVE_TYPE_REFERENCE_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("relationshipsByType", (ref = Map()) => ref.delete(action.referenceId))
      );
    case actionTypes.REMOVE_TEXT_PROPERTIES_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("textProperties", (ref = Map()) => ref.delete(action.referenceId))
      );
    case actionTypes.REMOVE_NUMERIC_PROPERTIES_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("numericProperties", (ref = Map()) => ref.delete(action.referenceId))
      );
    case actionTypes.REMOVE_BOOLEAN_PROPERTIES_FILTER:
      return state.update(action.filterId, (f = immutableRecords.FilterRecord()) =>
        f.update("boolProperties", (ref = Map()) => ref.delete(action.referenceId))
      );
    default:
      return state;
  }
};

const updateFilterAllIdWhenSettingNumViewsInner = (
  filterAllId,
  numViews,
  layout,
  viewsCount,
  activeViewId,
  newViewIds
) => {
  let newFilterState = filterAllId;
  if (viewsCount < numViews) {
    const diff = numViews - viewsCount;
    for (let i = 0; i < diff; i++) {
      newFilterState = newFilterState.push(newViewIds[i]);
    }
  } else if (viewsCount > numViews) {
    let nextViewId = activeViewId;
    let keepIds = [nextViewId];
    for (let i = 0; i < numViews - 1; i++) {
      nextViewId = nextViewInLayout(layout, nextViewId) || getFirstViewFromLayout(layout);
      keepIds.push(nextViewId);
    }
    newFilterState = newFilterState.filter(id => id === GLOBAL_FILTER_ID || keepIds.includes(id));
  }
  return newFilterState;
};

/**
 * This function can set more than two views.
 * Used to set three views that has been removed from the app.
 * @param {Immutable.Map} filterByIdState
 * @param {number} numViews Number of the views
 * @param {string[]} newViewIds
 * @param {TimelineRecord} globalState
 */
const updateFilterAllIdWhenSettingNumViews = (filterByIdState, numViews, newViewIds, globalState) => {
  const layout = appStateSelectors.getRootLayout(globalState);
  const viewsCount = appStateSelectors.getOpenViewCount(globalState);
  const activeViewId = appStateSelectors.getActiveViewId(globalState);
  return updateFilterAllIdWhenSettingNumViewsInner(
    filterByIdState,
    numViews,
    layout,
    viewsCount,
    activeViewId,
    newViewIds
  );
};

const allIds = (state = List(), action, globalState) => {
  switch (action.type) {
    case appStateActionTypes.SPLIT_VIEW_HORIZONTAL:
      return state.push(action.viewId);
    case appStateActionTypes.SPLIT_VIEW_VERTICAL:
      return state.push(action.viewId);
    case appStateActionTypes.REMOVE_VIEW:
      return state.filter(id => id !== action.viewId);
    case appStateActionTypes.SET_LAYOUT_SINGLE_VIEW:
    case appStateActionTypes.REMOVE_OTHER_VIEWS:
      return state.filter(id => id === GLOBAL_FILTER_ID || id === appStateSelectors.getActiveViewId(globalState));
    case appStateActionTypes.SET_LAYOUT_TWO_VIEW_STACK:
    case appStateActionTypes.SET_LAYOUT_TWO_VIEW_STACK_THIRDS:
    case appStateActionTypes.SET_LAYOUT_TWO_VIEW_SIDE: {
      return updateFilterAllIdWhenSettingNumViews(state, 2, [action.viewId], globalState);
    }
    //#!if readonly !== true
// case savedFilterActionTypes.SAVE_FILTER:
// return state.push(action.newFilterId);
// case savedFilterActionTypes.DELETE_SAVED_FILTER:
// return state.filter(id => id !== action.filterId);
    //#!endif
    default:
      return state;
  }
};

const finalReducer = combineReducersWithGlobal(createFromJS.IdReferenceList("FilterRecord", {}), {
  byId,
  allIds
});

export default finalReducer;
